import { Component, computed, effect, inject, input, resource, signal } from '@angular/core';
import { LocaleService, TranslatePipe } from '@cmi/addin-core';
import { LoadingSpinner, Notification } from '@cmi/ui';
import * as wordControls from '../../../lib/word-content-controls';
import { FeatureToggleService } from '../feature-toggle.service';
import { SelectorProviderService } from '../selector-provider.service';
import {
  CONST_PREFIX,
  DEFAULT_SUFFIX,
  KEY_SUFFIX,
  NAME_SUFFIX,
  ORDER_SUFFIX,
  SELECTOR_PREFIX,
  WIZARD_PREFIX
} from '../wizard-property.constants';

export type ParameterMode = 'wizard' | 'fixed' | 'selector';

@Component({
  selector: 'cmi-parameter-value-editor',
  imports: [TranslatePipe, LoadingSpinner, Notification],
  templateUrl: './parameter-value-editor.html'
})
export class ParameterValueEditor {
  private readonly _selectorProviderService = inject(SelectorProviderService);
  private readonly _featureToggleService = inject(FeatureToggleService);
  private readonly _localeService = inject(LocaleService);

  protected readonly supportsSelector = computed(() =>
    this._featureToggleService.supportsSelectorProviders()
  );

  // Inputs
  public readonly parameterName = input.required<string>();
  public readonly parameterType = input.required<'text' | 'date' | 'number' | 'boolean'>();

  // Document-property keys, all derived from the parameter name so they cannot drift apart
  private readonly _wizardKey = computed(() => `${WIZARD_PREFIX}${this.parameterName()}`);
  private readonly _constKey = computed(() => `${CONST_PREFIX}${this.parameterName()}`);
  private readonly _selectorKey = computed(() => `${SELECTOR_PREFIX}${this.parameterName()}`);

  // Mode
  public readonly mode = signal<ParameterMode>('wizard');

  // Wizard mode form fields
  public readonly uiLabel = signal('');
  public readonly textDefault = signal('');
  public readonly dateDefault = signal('');
  public readonly numberDefault = signal(0);
  public readonly boolDefault = signal(false);

  // Fixed mode form fields
  public readonly fixedText = signal('');
  public readonly fixedDate = signal('');
  public readonly fixedNumber = signal(0);
  public readonly fixedBoolean = signal(false);

  // Selector mode
  protected readonly selectedProvider = signal<string>('');

  // UI state
  protected readonly isSaving = signal(false);
  protected readonly saveError = signal<string | null>(null);

  private readonly _propertiesResource = resource({
    params: () => ({
      wizardKey: this._wizardKey(),
      nameKey: `${this._wizardKey()}${NAME_SUFFIX}`,
      defaultKey: `${this._wizardKey()}${DEFAULT_SUFFIX}`,
      fixedKey: this._constKey(),
      selectorKey: this._selectorKey(),
      selectorProviderKey: `${this._selectorKey()}${KEY_SUFFIX}`
    }),
    loader: async ({ params }) => {
      const [wizard, name, defaultValue, fixed, selector, selectorProvider] = await Promise.all([
        wordControls.getProperty(params.wizardKey),
        wordControls.getProperty(params.nameKey),
        wordControls.getProperty(params.defaultKey),
        wordControls.getProperty(params.fixedKey),
        wordControls.getProperty(params.selectorKey),
        wordControls.getProperty(params.selectorProviderKey)
      ]);
      return { wizard, name, defaultValue, fixed, selector, selectorProvider };
    }
  });

  protected readonly propertiesLoading = computed(() => this._propertiesResource.isLoading());
  protected readonly propertiesError = computed(() => this._propertiesResource.error());
  protected readonly isWizardSet = computed(() => !!this._propertiesResource.value()?.wizard);
  protected readonly isFixedSet = computed(() => !!this._propertiesResource.value()?.fixed);
  protected readonly isSelectorSet = computed(() => !!this._propertiesResource.value()?.selector);
  protected readonly isSelectorReadOnly = computed(
    () => this.isSelectorSet() && !this.supportsSelector()
  );

  protected readonly selectorProviders = computed(() => this._selectorProviderService.providers());
  protected readonly selectorProvidersLoading = computed(() =>
    this._selectorProviderService.isLoading()
  );
  protected readonly selectorProvidersError = computed(() => this._selectorProviderService.error());

  public constructor() {
    effect(() => {
      const props = this._propertiesResource.value();
      if (!props) {
        return;
      }
      const type = this.parameterType();

      if (props.wizard) {
        this.mode.set('wizard');
        this.uiLabel.set(props.name?.value ?? '');
        if (type === 'date') {
          this.dateDefault.set(props.defaultValue?.value ?? '');
        } else if (type === 'boolean') {
          this.boolDefault.set(props.wizard.value === 'true');
        } else if (type === 'number') {
          const num = Number(props.wizard.value);
          this.numberDefault.set(isNaN(num) ? 0 : num);
        } else {
          this.textDefault.set(props.wizard.value === '-' ? '' : props.wizard.value);
        }
      } else if (props.fixed) {
        this.mode.set('fixed');
        if (type === 'boolean') {
          this.fixedBoolean.set(Boolean(props.fixed.value));
        } else if (type === 'number') {
          const num = Number(props.fixed.value);
          this.fixedNumber.set(isNaN(num) ? 0 : num);
        } else if (type === 'date') {
          this.fixedDate.set(props.fixed.value ?? '');
        } else {
          this.fixedText.set(props.fixed.value ?? '');
        }
      } else if (props.selector) {
        this.mode.set('selector');
        this.selectedProvider.set(props.selectorProvider?.value ?? '');
      }
    });
  }

  public onUiLabelInput(event: Event): void {
    this.uiLabel.set((event.target as HTMLInputElement).value);
  }

  public onTextDefaultInput(event: Event): void {
    this.textDefault.set((event.target as HTMLInputElement).value);
  }

  public onDateDefaultInput(event: Event): void {
    this.dateDefault.set((event.target as HTMLInputElement).value);
  }

  public onNumberDefaultInput(event: Event): void {
    this.numberDefault.set(Number((event.target as HTMLInputElement).value));
  }

  public onBoolDefaultChange(event: Event): void {
    this.boolDefault.set((event.target as HTMLInputElement).checked);
  }

  public onFixedTextInput(event: Event): void {
    this.fixedText.set((event.target as HTMLInputElement).value);
  }

  public onFixedDateChange(event: Event): void {
    this.fixedDate.set((event.target as HTMLInputElement).value);
  }

  public onFixedNumberInput(event: Event): void {
    this.fixedNumber.set(Number((event.target as HTMLInputElement).value));
  }

  public onFixedBooleanChange(event: Event): void {
    this.fixedBoolean.set((event.target as HTMLInputElement).checked);
  }

  protected onProviderChange(event: Event): void {
    this.selectedProvider.set((event.target as HTMLSelectElement).value);
  }

  protected reloadSelectorProviders(): void {
    this._selectorProviderService.reload();
  }

  public async save(): Promise<void> {
    this.saveError.set(null);
    // A fixed value without a value is meaningless, and an empty date would silently write nothing
    if (this.mode() === 'fixed' && this.parameterType() === 'date' && !this.fixedDate().trim()) {
      this.saveError.set(this._localeService.translate('parameterSettings.fixedDateRequired'));
      return;
    }

    this.isSaving.set(true);
    const isNew = !this.isWizardSet() && !this.isFixedSet() && !this.isSelectorSet();
    try {
      if (this.mode() === 'wizard') {
        await this._saveWizard();
      } else if (this.mode() === 'selector') {
        await this._saveSelector();
      } else {
        await this._saveFixed();
      }
      if (isNew) {
        await wordControls.appendParamToAllContentControlTags(this.parameterName());
      }
      this._propertiesResource.reload();
    } catch (error) {
      this.saveError.set(error instanceof Error ? error.message : String(error));
    } finally {
      this.isSaving.set(false);
    }
  }

  private async _saveWizard(): Promise<void> {
    const type = this.parameterType();
    const wizardKey = this._wizardKey();

    const label = this.uiLabel().trim();
    if (label) {
      await wordControls.writeProperty(`${wizardKey}${NAME_SUFFIX}`, label);
    } else {
      await wordControls.deleteProperty(`${wizardKey}${NAME_SUFFIX}`);
    }

    // The base property carries data type and suggested value, the server needs it to build the
    // DocProperty. Only dates keep a .Default, their base property holds a sentinel date instead.
    if (type === 'date') {
      await wordControls.writePropertyTyped(wizardKey, new Date(Date.UTC(1900, 0, 1)));
      const dateDefaultVal = this.dateDefault().trim();
      if (dateDefaultVal) {
        await wordControls.writeProperty(`${wizardKey}${DEFAULT_SUFFIX}`, dateDefaultVal);
      } else {
        await wordControls.deleteProperty(`${wizardKey}${DEFAULT_SUFFIX}`);
      }
    } else if (type === 'boolean') {
      await wordControls.writePropertyTyped(wizardKey, this.boolDefault());
    } else if (type === 'number') {
      await wordControls.writePropertyTyped(wizardKey, this.numberDefault());
    } else {
      await wordControls.writeProperty(wizardKey, this.textDefault().trim() || '-');
    }

    await wordControls.deleteProperty(this._constKey());
    await wordControls.deleteProperty(this._selectorKey());
    await wordControls.deleteProperty(`${this._selectorKey()}${KEY_SUFFIX}`);

    // Assign .Order if not already set (e.g. switching from fixed to wizard)
    const orderKey = `${wizardKey}${ORDER_SUFFIX}`;
    const existingOrder = await wordControls.getProperty(orderKey);
    if (!existingOrder) {
      const allProperties = await wordControls.getCustomProperties();
      const maxOrder = allProperties
        .filter((p) => p.key.startsWith(WIZARD_PREFIX) && p.key.endsWith(ORDER_SUFFIX))
        .reduce((max, p) => Math.max(max, Number(p.value)), -1);
      await wordControls.writePropertyTyped(orderKey, maxOrder + 1);
    }
  }

  private async _saveFixed(): Promise<void> {
    const type = this.parameterType();
    const fixedKey = this._constKey();

    if (type === 'date') {
      const dateStr = this.fixedDate().trim();
      if (dateStr) {
        const [year, month, day] = dateStr.split('-').map(Number);
        await wordControls.writePropertyTyped(fixedKey, new Date(Date.UTC(year, month - 1, day)));
      }
    } else if (type === 'boolean') {
      await wordControls.writePropertyTyped(fixedKey, this.fixedBoolean());
    } else if (type === 'number') {
      await wordControls.writePropertyTyped(fixedKey, this.fixedNumber());
    } else {
      await wordControls.writeProperty(fixedKey, this.fixedText().trim() || '-');
    }

    await wordControls.deleteProperty(this._wizardKey());
    await wordControls.deleteProperty(`${this._wizardKey()}${NAME_SUFFIX}`);
    await wordControls.deleteProperty(`${this._wizardKey()}${DEFAULT_SUFFIX}`);
    await wordControls.deleteProperty(`${this._wizardKey()}${ORDER_SUFFIX}`);
    await wordControls.deleteProperty(this._selectorKey());
    await wordControls.deleteProperty(`${this._selectorKey()}${KEY_SUFFIX}`);
  }

  private async _saveSelector(): Promise<void> {
    const type = this.parameterType();
    const providerKey = this.selectedProvider();
    const selectorBase = this._selectorKey();

    if (type === 'date') {
      await wordControls.writePropertyTyped(selectorBase, new Date(Date.UTC(1900, 0, 1)));
    } else if (type === 'boolean') {
      await wordControls.writePropertyTyped(selectorBase, true);
    } else if (type === 'number') {
      await wordControls.writePropertyTyped(selectorBase, 0);
    } else {
      await wordControls.writeProperty(selectorBase, '-');
    }

    await wordControls.writeProperty(`${selectorBase}${KEY_SUFFIX}`, providerKey);

    await wordControls.deleteProperty(this._constKey());
    await wordControls.deleteProperty(this._wizardKey());
    await wordControls.deleteProperty(`${this._wizardKey()}${NAME_SUFFIX}`);
    await wordControls.deleteProperty(`${this._wizardKey()}${DEFAULT_SUFFIX}`);
    await wordControls.deleteProperty(`${this._wizardKey()}${ORDER_SUFFIX}`);
  }
}
